// Открытие витрины сразу на позиции: по адресу страницы (/product/12-…/, /bouquet/…,
// /catalog/…; старые /tovar/ /buket/ /katalog/ тоже) или по start_param из бота (p_12, b_45, c_3).
//
// Страницу позиции отдаёт сервер (backend/app/routers/seo_pages.py) — это index.html
// витрины с карточкой внутри. Витрина стартует как обычно, а нужная карточка
// забирает цель через takeDeepLink() и открывается сама — один раз.
//
// Адрес в строке браузера ведём только на сайте: открыли карточку — /product/…,
// закрыли или «Назад» — обратно. В мини-аппах Telegram/Max адрес не трогаем: его
// никто не видит, а лишние записи истории ломают системный «Назад» на Android.
//
// Кроме позиций (29.10.2026, ТЗ «реклама по наличию», В1–В3):
//   { kind: 'missing' }              — сервер ответил «Такой страницы нет» (метка novaflo-page);
//   { kind: 'product', gone: true }  — ?gone=p12: архивную позицию сервер увёл в её раздел;
//   { kind: 'collection', id: slug } — подборка /podborka/<slug>/.
// Позиции, которой нет в списке витрины, карточки нет — её окно «Сейчас нет в продаже»
// показывает каталог (MenuPage / BouquetCatalog, components/UnavailableNotice.jsx).
import { useEffect, useRef } from 'react';
import { getPlatform, getStartParam } from '../platform';
import { itemPath, parseItemPath, parseStartParam, parseCollectionPath, parseGoneParam } from './productUrl';
import { trackPageView } from './metrika';

let target = null;
const consumed = new Set();

// Сервер отдал страницу «Такой страницы нет» (backend seo_pages.not_found_page) — адрес вроде
// /product/abc/ витрина сама не разберёт, поэтому сервер оставляет метку в <head>
function serverNotFound() {
  if (typeof document === 'undefined') return false;
  const meta = document.querySelector('meta[name="novaflo-page"]');
  return !!meta && meta.getAttribute('content') === 'not-found';
}

export function getDeepLinkTarget() {
  if (target) return target;
  if (typeof window === 'undefined') return null;
  const { pathname, search } = window.location;
  // Подборка — раньше метки 404: неизвестную сервер отдаёт «Такой страницы нет» (для поиска),
  // а витрина показывает окно «Сейчас нет в продаже» с похожими из других подборок (ТЗ В3).
  // start_param у Max появляется, когда догрузится SDK, — поэтому не кэшируем «ничего»
  target = parseCollectionPath(pathname)
    || (serverNotFound() ? { kind: 'missing' } : null)
    || parseGoneParam(search)
    || parseItemPath(pathname)
    || parseStartParam(getStartParam());
  return target;
}

// Человек ушёл со страницы, на которую пришёл (вышел из подборки в каталог): ссылка отработала,
// при возврате в каталог (после корзины) витрина не открывает её заново
export function finishDeepLink() {
  target = { kind: 'done' };
}

// Для тестов: забыть цель и открытые по ней окна
export function resetDeepLinkForTests() {
  target = null;
  consumed.clear();
}

// true ровно один раз для совпавшей позиции: карточка открывается при первом показе,
// а не при каждом возвращении в каталог
export function takeDeepLink(kind, id) {
  const t = getDeepLinkTarget();
  if (!t || t.kind !== kind || t.id !== id) return false;
  const key = `${kind}:${id}`;
  if (consumed.has(key)) return false;
  consumed.add(key);
  return true;
}

export function isWebAddressBar() {
  return getPlatform() === 'web' && typeof window !== 'undefined' && !!window.history?.pushState;
}

// Адрес категории в строке браузера: чипы не плодят записи истории, только заменяют адрес
export function replaceCategoryUrl(category) {
  if (!isWebAddressBar()) return;
  const onItemPage = /^\/(product|bouquet|catalog|tovar|buket|katalog|podborka)\//.test(window.location.pathname);
  const path = category ? itemPath('category', category.id, category.name) : '/';
  if (!category && !onItemPage) return;
  window.history.replaceState(window.history.state, '', path + window.location.search);
}

// Закрыли окно «Сейчас нет в продаже» / «Такой страницы нет» или вышли из подборки: в строке
// браузера — раздел (или главная), без метки gone; заголовок вкладки — магазина. Записи истории
// не добавляем: пришедшего из поиска или рекламы «Назад» уводит туда, откуда он пришёл.
export function leaveLandingUrl(category) {
  if (!isWebAddressBar()) return;
  const path = category ? itemPath('category', category.id, category.name) : '/';
  const params = new URLSearchParams(window.location.search);
  params.delete('gone');
  const q = params.toString();
  window.history.replaceState(window.history.state, '', path + (q ? `?${q}` : ''));
  if (process.env.REACT_APP_BRAND_TITLE) document.title = process.env.REACT_APP_BRAND_TITLE;
}

// Карточка позиции открыта → её адрес в строке браузера; «Назад» закрывает карточку.
export function useItemUrl(open, kind, item, close) {
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!open || !item || !isWebAddressBar()) return undefined;
    const path = itemPath(kind, item.id, item.name);
    // Пришли по ссылке на эту позицию — запись истории уже есть, своей не добавляем
    const landed = window.location.pathname === path;
    let pushed = false;
    if (!landed) {
      window.history.pushState({ stemsItem: path }, '', path);
      pushed = true;
      trackPageView();
    }
    const onPop = () => {
      pushed = false;
      closeRef.current && closeRef.current();
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (pushed) {
        window.history.back();
      } else if (landed && window.location.pathname === path) {
        // Закрыли карточку, на которую пришли из поиска или по ссылке, — дальше обычный каталог
        window.history.replaceState(window.history.state, '', '/' + window.location.search);
        if (process.env.REACT_APP_BRAND_TITLE) document.title = process.env.REACT_APP_BRAND_TITLE;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}
